import { type ButtonHTMLAttributes, type ReactNode, type Ref } from 'react';
import clsx from 'clsx';
import './AudioButton.css';

export interface AudioButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Currently playing — shows a ring-pulse halo. */
  playing?: boolean;
  size?: 'sm' | 'md' | 'lg';
  /**
   * Visual + semantic variant.
   * - `default` — primary terracotta speaker (play at normal speed).
   * - `slow` — secondary, muted button with a turtle icon (🐢 slow replay).
   */
  variant?: 'default' | 'slow';
  /** Override the default icon (speaker, or turtle for the slow variant). */
  icon?: ReactNode;
  /** Short screen-reader label (Georgian default; depends on variant). */
  label?: string;
  ref?: Ref<HTMLButtonElement>;
}

/**
 * AudioButton — circular audio control. The `default` variant is the primary
 * terracotta speaker (normal-speed playback); the `slow` variant is a smaller,
 * muted turtle button for a slowed-down replay of the same phrase.
 */
export function AudioButton({
  playing,
  size = 'md',
  variant = 'default',
  icon,
  label,
  type,
  className,
  ref,
  ...rest
}: AudioButtonProps) {
  const isSlow = variant === 'slow';
  const resolvedLabel = label ?? (isSlow ? 'ნელა მოუსმინე' : 'მოუსმინე');
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={clsx('fluent-audiobtn', className)}
      data-size={size}
      data-variant={variant}
      data-playing={playing || undefined}
      aria-label={resolvedLabel}
      aria-pressed={playing || undefined}
      {...rest}
    >
      <span className="fluent-audiobtn__icon" aria-hidden="true">
        {icon ?? (isSlow ? <TurtleIcon /> : <SpeakerIcon />)}
      </span>
    </button>
  );
}

function SpeakerIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
      <path d="M4 10v4a1 1 0 0 0 1 1h3l4 4a1 1 0 0 0 1.7-.7V5.7A1 1 0 0 0 12 5l-4 4H5a1 1 0 0 0-1 1z" />
      <path d="M16 8c1.5 1 2 2.5 2 4s-.5 3-2 4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M18.5 5.5c2.5 1.7 3.5 4 3.5 6.5s-1 4.8-3.5 6.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/** Turtle — the universal "slow" affordance (Duolingo-style). Solid
 *  silhouette so it reads clearly at small sizes on any background. */
function TurtleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
      {/* shell */}
      <path d="M5 15.5a7 5 0 0 1 14 0z" />
      {/* head */}
      <path d="M19 14.8c1.4 0 2.4-.8 2.4-1.8s-1-1.6-2.4-1.5z" />
      {/* front + back legs */}
      <path d="M6.8 18.6c0 .8-.6 1.4-1.4 1.4M17 18.6c0 .8.6 1.4 1.4 1.4" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}
